import { useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { FileText, RefreshCw } from 'lucide-react'
import { Link } from 'react-router'
import type { WpArticle } from '@/api/hooks/use-wordpress'
import { useWpArticles } from '@/api/hooks/use-wordpress'

const PREVIEW_LIMIT = 5

export function SyncedArticlesCard(): React.JSX.Element {
  const { data: articlesData, isLoading } = useWpArticles()
  const qc = useQueryClient()
  const articles = articlesData?.data ?? []

  const firstArticle = articles[0]
  const latestSync = firstArticle
    ? articles.reduce(
        (latest: string, a: WpArticle) => (a.synced_at > latest ? a.synced_at : latest),
        firstArticle.synced_at,
      )
    : null

  function handleSyncNow(): void {
    qc.invalidateQueries({ queryKey: ['wp-articles'] })
  }

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-brand-500" />
          <h2 className="text-lg font-semibold text-text-primary">Synced Articles</h2>
        </div>
        <div className="flex items-center gap-3 text-xs text-text-secondary">
          {!isLoading && (
            <span>
              {articles.length} article{articles.length !== 1 ? 's' : ''}
            </span>
          )}
          {latestSync && (
            <span>Last sync {formatDistanceToNow(new Date(latestSync), { addSuffix: true })}</span>
          )}
        </div>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <p className="text-sm text-text-tertiary">Loading articles...</p>
        ) : articles.length === 0 ? (
          <p className="py-4 text-center text-sm text-text-tertiary">
            No articles synced yet. Articles will appear here after your WordPress plugin syncs.
          </p>
        ) : (
          <ArticlePreviewList articles={articles} />
        )}
      </div>

      {articles.length > 0 && (
        <div className="mt-4 flex items-center gap-3 border-t border-surface-3 pt-4">
          <Link
            to="/wordpress"
            className="rounded-md border border-surface-3 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-1"
          >
            View All Articles
          </Link>
          <button
            type="button"
            onClick={handleSyncNow}
            className="flex items-center gap-1.5 rounded-md border border-surface-3 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-1"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Sync Now
          </button>
        </div>
      )}
    </div>
  )
}

function ArticlePreviewList({ articles }: { articles: WpArticle[] }): React.JSX.Element {
  const visible = articles.slice(0, PREVIEW_LIMIT)
  const remaining = articles.length - PREVIEW_LIMIT

  return (
    <ul className="space-y-2">
      {visible.map((article) => (
        <li key={article.id} className="flex items-center gap-2 text-sm">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-400" />
          <span className="truncate text-text-primary">{article.title}</span>
        </li>
      ))}
      {remaining > 0 && (
        <li className="text-sm text-text-tertiary">
          ... {remaining} more article{remaining !== 1 ? 's' : ''}
        </li>
      )}
    </ul>
  )
}
